/**
 * Subtle "Version history" surface for the public client portal — anchored to the
 * 3D Model and Engineered Drawings step actions in `ClientPortalSplash`. Lists
 * release bundles (single- or multi-file) newest first.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { History, X, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import type { PortalFileRelease, PortalReleaseBundle } from '../../types';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';
import { formatPortalReleaseAddedAt } from '../../utils/portalReleaseDateFormat';
import './PortalReleasedVersionsPopover.css';

export interface PortalReleasedVersionsPopoverProps {
  bundles: PortalReleaseBundle[];
  releases: PortalFileRelease[];
  heading: string;
  onOpenRelease?: (release: PortalFileRelease, bundle?: PortalReleaseBundle) => void;
}

const PortalReleasedVersionsPopover: React.FC<PortalReleasedVersionsPopoverProps> = ({
  bundles,
  releases,
  heading,
  onOpenRelease,
}) => {
  const [open, setOpen] = useState(false);
  /** Multi-file bundles start expanded; track which the user collapsed. */
  const [collapsedBundleVersions, setCollapsedBundleVersions] = useState<Set<number>>(
    () => new Set(),
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const firstOpenLinkRef = useRef<HTMLAnchorElement | null>(null);
  const setFirstOpenLinkEl = useCallback((el: HTMLAnchorElement | null) => {
    firstOpenLinkRef.current = el;
  }, []);

  const releaseById = useMemo(() => {
    const map = new Map<string, PortalFileRelease>();
    for (const r of releases) {
      if (r.id) map.set(r.id, r);
    }
    return map;
  }, [releases]);

  const sorted = useMemo(
    () => [...bundles].sort((a, b) => b.releaseVersionIndex - a.releaseVersionIndex),
    [bundles],
  );

  const close = useCallback(() => {
    setOpen(false);
    setCollapsedBundleVersions(new Set());
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'Tab' && popoverRef.current) {
        const focusables = popoverRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => firstOpenLinkRef.current?.focus());
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (sorted.length === 0) return null;

  const handleOpenRelease = (r: PortalFileRelease, bundle?: PortalReleaseBundle) => {
    onOpenRelease?.(r, bundle);
  };

  const triggerLabel =
    sorted.length === 1
      ? `Version ${sorted[0]!.releaseVersionIndex}`
      : `Version history (${sorted.length})`;

  const resolveBundleReleases = (bundle: PortalReleaseBundle): PortalFileRelease[] =>
    bundle.releaseIds
      .map(id => releaseById.get(id))
      .filter((r): r is PortalFileRelease => !!r);

  const dialog = open
    ? createPortal(
        <div
          className="portal-released-pop-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) close();
          }}
        >
          <div
            ref={popoverRef}
            className="portal-released-pop-panel"
            role="dialog"
            aria-modal="true"
            aria-label={`${heading} version history`}
          >
            <header className="portal-released-pop-header">
              <span className="portal-released-pop-heading">
                <History size={14} aria-hidden /> {heading}
              </span>
              <button
                type="button"
                className="portal-released-pop-close"
                onClick={close}
                aria-label="Close version history"
              >
                <X size={16} aria-hidden />
              </button>
            </header>
            <ul className="portal-released-pop-list">
              {sorted.map((bundle, i) => {
                const isFirst = i === 0;
                const bundleReleases = resolveBundleReleases(bundle);
                const primary =
                  bundleReleases.find(r => r.id === bundle.primaryReleaseId) || bundleReleases[0];
                const isMulti = bundleReleases.length > 1;
                const expanded = isMulti && !collapsedBundleVersions.has(bundle.releaseVersionIndex);

                if (!primary) return null;

                if (!isMulti) {
                  const href = portalHostedAssetHref(primary.releasedUrl, { downloadName: primary.name });
                  return (
                    <li key={bundle.id || `b-${bundle.releaseVersionIndex}`} className="portal-released-pop-row">
                      <span className="portal-released-pop-row-main">
                        <span className="portal-released-pop-name" title={primary.name}>
                          {primary.name}
                        </span>
                        <span className="portal-released-pop-meta">
                          <span className="portal-released-pop-vchip">v{bundle.releaseVersionIndex}</span>
                          <span aria-hidden> · </span>
                          <span>{formatPortalReleaseAddedAt(bundle.releasedAt) ?? '—'}</span>
                          {isFirst ? <span className="portal-released-pop-current">Current</span> : null}
                        </span>
                      </span>
                      <a
                        ref={isFirst ? setFirstOpenLinkEl : undefined}
                        className="portal-released-pop-open"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleOpenRelease(primary, bundle)}
                        aria-label={`Open ${primary.name}`}
                      >
                        Open <ExternalLink size={12} aria-hidden />
                      </a>
                    </li>
                  );
                }

                return (
                  <li
                    key={bundle.id || `b-${bundle.releaseVersionIndex}`}
                    className="portal-released-pop-row portal-released-pop-row--bundle"
                  >
                    <div className="portal-released-pop-bundle-head">
                      <button
                        type="button"
                        className="portal-released-pop-bundle-toggle"
                        onClick={() =>
                          setCollapsedBundleVersions(prev => {
                            const next = new Set(prev);
                            if (next.has(bundle.releaseVersionIndex)) {
                              next.delete(bundle.releaseVersionIndex);
                            } else {
                              next.add(bundle.releaseVersionIndex);
                            }
                            return next;
                          })
                        }
                        aria-expanded={expanded}
                      >
                        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        <span className="portal-released-pop-name">
                          {bundle.displayLabel} — v{bundle.releaseVersionIndex}
                        </span>
                        <span className="portal-released-pop-bundle-count">
                          {bundleReleases.length} files
                        </span>
                        {isFirst ? <span className="portal-released-pop-current">Current</span> : null}
                      </button>
                      <a
                        ref={isFirst ? setFirstOpenLinkEl : undefined}
                        className="portal-released-pop-open"
                        href={portalHostedAssetHref(primary.releasedUrl, { downloadName: primary.name })}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleOpenRelease(primary, bundle)}
                        aria-label={`Open primary file ${primary.name}`}
                      >
                        Open primary <ExternalLink size={12} aria-hidden />
                      </a>
                    </div>
                    {expanded && (
                      <ul className="portal-released-pop-bundle-files">
                        {bundleReleases.map(r => (
                          <li key={r.id} className="portal-released-pop-bundle-file">
                            <span className="portal-released-pop-name" title={r.name}>
                              {r.displayLabel || r.name}
                            </span>
                            <a
                              className="portal-released-pop-open"
                              href={portalHostedAssetHref(r.releasedUrl, { downloadName: r.name })}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => handleOpenRelease(r, bundle)}
                              aria-label={`Open ${r.name}`}
                            >
                              Open <ExternalLink size={12} aria-hidden />
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <span className="portal-released-pop">
      <button
        ref={triggerRef}
        type="button"
        className="portal-released-pop-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${triggerLabel} for ${heading}`}
        onClick={() => setOpen(o => !o)}
      >
        <History size={13} aria-hidden /> {triggerLabel}
      </button>
      {dialog}
    </span>
  );
};

export default PortalReleasedVersionsPopover;
